import { useTranslation } from 'react-i18next'
import type { ProductPage } from '../lib/productView'
import { productPreviewVoteFeature } from '../lib/previewVoteFeature'
import type { Product } from '../lib/types'
import { Card, CardBody } from './ui/Card'
import { VotePreviewCard } from './VotePreviewCard'

/** A product's preview card — the prototype's `tab-volume-mcp-preview` /
 * `tab-volume-agents-preview` panel: `.card > .card-body >
 * .not-subscribed-state` with the "Preview" badge, the product name, a 480px
 * description ("Your account does not have an active subscription for
 * {product}." + the product pitch), the "Contact us" lock pill and the
 * thumbs-up / thumbs-down votes (VotePreviewCard owns the shell). `page`
 * only names the vote's surface (lib/previewVoteFeature.ts).
 *
 * Shown for every `preview` cell of the status matrix (lib/productView.ts).
 * Mambu MCP on Subscription and Usage has no prototype preview panel; it
 * reuses this same Additional Volume MCP card and copy (MS-819 Q14, decided
 * by the PO 2026-10-07; ARCHITECTURE_DECISIONS.md §42). */
export function NotContractedPreview({ product, page }: Readonly<{ product: Product; page: ProductPage }>) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardBody>
        <VotePreviewCard
          badgeText={t('notContracted.badge')}
          title={product}
          // One key per product holding the whole paragraph, so translators
          // can order and join its sentences their own way.
          description={t(product === 'Mambu MCP' ? 'notContracted.bodyMcp' : 'notContracted.bodyAgt', {
            product,
          })}
          pillText={t('notContracted.contactUs')}
          feature={productPreviewVoteFeature(product, page)}
          featureName={product}
          descriptionMaxWidthClassName="max-w-[480px]"
        />
      </CardBody>
    </Card>
  )
}
